import { BookingType, Shop } from './mrCapTypes';

/**
 * @param num Number to format
 * @param precision Decimals to use (if present)
 * @param separator Format separator
 */
export function formatNumber(num: number, precision = 0, separator = ' ') {
  if (!isFinite(num)) {
    return num.toString();
  }

  let n = num.toFixed(precision || 0).split('.');
  n[0] = n[0].replace(/\d(?=(\d{3})+$)/g, '$&' + (separator || ','));
  return n.join('.');
}

interface ClassNameObj {
  [name: string]: boolean | undefined | null;
}

export function className(...params: (string | ClassNameObj)[]) {
  const res: string[] = [];

  for (let param of params) {
    if (typeof param === 'string') {
      res.push(param);
    } else if (typeof param === 'object') {
      const keys = Object.keys(param).filter(key => (param as ClassNameObj)[key]);
      if (keys.length) {
        res.push(...keys);
      }
    }
  }

  return res.join(' ');
}

export enum KeyCodes {
  enter = 13,
  escape = 27
}

export function autobind(target: any, propKey: string): TypedPropertyDescriptor<any> {
  const func = target[propKey];

  return {
    get() {
      const bound = func.bind(this);

      Object.defineProperty(this, propKey, {
        configurable: true,
        enumerable: false,
        writable: true,
        value: bound
      });

      return bound;
    }
  };
}

export interface DebounceSettings {
  immediate?: boolean;
  autoBind?: boolean;
}

export function debounce(wait: number, { autoBind, immediate }: DebounceSettings = {}) {
  return function (target: any, propKey: string): TypedPropertyDescriptor<any> {
    const func = target[propKey];

    return {
      get() {
        let debFunc = doDebounce(func, wait, immediate);

        if (autoBind) {
          debFunc = debFunc.bind(this);
        }

        Object.defineProperty(this, propKey, {
          configurable: true,
          enumerable: false,
          writable: true,
          value: debFunc
        });

        return debFunc;
      }
    };
  };
}

export function doDebounce<T extends Function>(func: T, wait: number, immediate = false): T {
  let timeout: number;

  const res = function (this: any) {
    let that = this;
    let args: any[] = [];

    for (let i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }

    const later = () => {
      timeout = 0;
      if (!immediate) {
        func.apply(that, args);
      }
    };

    let callNow = immediate && !timeout;

    clearTimeout(timeout);
    timeout = setTimeout(later, wait) as any;

    if (callNow) {
      func.apply(that, args);
    }
  };

  return res as any;
}

export function intoQueryString(url: string, query: { [key: string]: string | number | null | undefined | boolean }) {
  return url + '?' +
    Object.keys(query)
      .filter(key => query[key])
      .map(key => `${key}=${query[key]}`)
      .join('&');
}

export function getEnumValuesFromString<T>(enumType: T, str: string): T[] {
  let et = enumType as any;
  let typesStr = (str ?? '').toLowerCase().split(',');
  let types: T[] = [];

  for (let key of Object.keys(et)) {
    let val = et[key];

    if (typeof val === 'string' && typesStr.includes(val.toLowerCase())) {
      types.push(et[val]);
    }
  }

  return types;
}

let campaignCode: string | null = null;

export function setCampaignCode(code: string) {
  campaignCode = code;
}

export function getCampaignId() {
  if (campaignCode) {
    return campaignCode;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('c') ?? '';
}

export function getCampaignBookingTypes(paymentTypes: string) {
  return getEnumValuesFromString<BookingType>(BookingType as any, paymentTypes);
}

export function getShopSlug() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('station') ?? searchParams.get('anl') ?? searchParams.get('shop');
}

export function getShopBySlug(shops: Shop[]) {
  const stationSlug = getShopSlug();

  if (!stationSlug) {
    return null;
  }

  return shops.find(s => s.slug === stationSlug);
}